/**
 * Pure helpers for the super admin panel (docs/phase-4-plan.md).
 * No Firebase, no i18n. Patches use `undefined` for "remove this field";
 * useListingActions().updateListing turns that into a Firestore delete.
 */
import type { CategoryKey, Listing, ListingStatus } from '../types';
import { displayName, matchesQuery, normalizeSearchText } from './directorySearch';

/** Every status, in the order the super admin sees them. */
export const LISTING_STATUSES: ListingStatus[] = ['pending', 'draft', 'published', 'hidden', 'rejected', 'deleted'];

/** Max length of a rejection reason; the Firestore rules check the same number. */
export const REJECTION_REASON_MAX = 500;

export interface SuperDirectoryFilter {
  category: CategoryKey | 'all';
  status: ListingStatus | 'all';
  /** Only listings nobody owns. */
  noOwner: boolean;
  search: string;
}

export const EMPTY_SUPER_FILTER: SuperDirectoryFilter = {
  category: 'all',
  status: 'all',
  noOwner: false,
  search: '',
};

/** Super admin Directory tab: filter, then sort by name in the current language. */
export function filterSuperDirectory(
  listings: Listing[],
  filter: SuperDirectoryFilter,
  isHebrew: boolean
): Listing[] {
  const query = normalizeSearchText(filter.search);
  return listings
    .filter((l) => filter.category === 'all' || l.category === filter.category)
    .filter((l) => filter.status === 'all' || l.status === filter.status)
    .filter((l) => !filter.noOwner || (l.ownerUids ?? []).length === 0)
    .filter(
      (l) =>
        !query ||
        matchesQuery(normalizeSearchText(`${l.nameHe ?? ''} ${l.nameEn ?? ''} ${l.id}`), filter.search)
    )
    .sort((a, b) =>
      displayName(a, isHebrew).localeCompare(displayName(b, isHebrew), isHebrew ? 'he' : 'en')
    );
}

/** How many listings have each status. */
export function countByStatus(listings: Listing[]): Record<ListingStatus, number> {
  const counts = Object.fromEntries(LISTING_STATUSES.map((s) => [s, 0])) as Record<ListingStatus, number>;
  for (const l of listings) {
    if (l.status in counts) counts[l.status] += 1;
  }
  return counts;
}

/** Approvals list: pending listings, oldest first (first come, first served). */
export function pendingQueue(listings: Listing[]): Listing[] {
  return listings
    .filter((l) => l.status === 'pending')
    .sort((a, b) => (a.createdAt ?? '').localeCompare(b.createdAt ?? ''));
}

// ---------- Status changes ----------

export type StatusPatch = { status: ListingStatus; rejectionReason?: string | undefined };

/** Super admin approves: published, and any old rejection reason is removed. */
export function approvePatch(): StatusPatch {
  return { status: 'published', rejectionReason: undefined };
}

/** Super admin rejects, with an optional reason the person sees in My Items. */
export function rejectPatch(reason: string | undefined): StatusPatch {
  const trimmed = (reason ?? '').trim().slice(0, REJECTION_REASON_MAX);
  return { status: 'rejected', rejectionReason: trimmed || undefined };
}

/** Owner fixed a rejected listing and sends it again. */
export function resubmitPatch(): StatusPatch {
  return { status: 'pending', rejectionReason: undefined };
}

/** Publish or hide (any status → published / hidden). Clears a rejection reason. */
export function visibilityPatch(visible: boolean): StatusPatch {
  return { status: visible ? 'published' : 'hidden', rejectionReason: undefined };
}

/**
 * Owner deletes their listing (plan 16): hidden everywhere and gone from My Items,
 * but kept for the super admin, who can bring it back or delete it for good.
 */
export function ownerDeletePatch(current: ListingStatus, nowIso: string) {
  return { status: 'deleted' as ListingStatus, statusBeforeDelete: current, deletedAt: nowIso };
}

/** Super admin brings a deleted listing back, with the status it had (a deleted status becomes hidden). */
export function restorePatch(listing: Pick<Listing, 'statusBeforeDelete'>) {
  const before = listing.statusBeforeDelete;
  return {
    status: (before && before !== 'deleted' ? before : 'hidden') as ListingStatus,
    statusBeforeDelete: undefined,
    deletedAt: undefined,
  };
}

// ---------- Owners ----------

/** Adds an owner once; keeps the existing order. */
export function withOwnerAdded(ownerUids: string[] | undefined, uid: string): string[] {
  const list = ownerUids ?? [];
  return list.includes(uid) ? [...list] : [...list, uid];
}

export function withOwnerRemoved(ownerUids: string[] | undefined, uid: string): string[] {
  return (ownerUids ?? []).filter((u) => u !== uid);
}

/** Listings owned by this user (People tab). */
export function listingsOwnedBy(listings: Listing[], uid: string): Listing[] {
  return listings.filter((l) => (l.ownerUids ?? []).includes(uid));
}

// ---------- Invites ----------

/** Very small email check: something@something.something, no spaces. */
export function looksLikeEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/** The link a person opens to accept an invite, on whatever address the site runs. */
export function inviteLink(origin: string, token: string): string {
  return `${origin.replace(/\/+$/, '')}/join/${encodeURIComponent(token)}`;
}
